"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Cpu, Fan, Layers, ShieldAlert, Wrench, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const packageItems = [{
  title: "Bull & Motion Base",
  desc: "The primary mechanical assembly. Confirm that the complete bull system shown in the listing is included.",
  icon: Cpu
}, {
  title: "Operator Controller",
  desc: "Check which controller comes with the exact machine and whether its condition or model information is available.",
  icon: Wrench
}, {
  title: "Inflatable Safety Arena",
  desc: "Confirm whether the inflatable shown in the listing is included in the advertised price.",
  icon: ShieldAlert
}, {
  title: "Blower",
  desc: "Check whether the correct blower for the inflatable comes with the package.",
  icon: Fan
}, {
  title: "Cables & Accessories",
  desc: "Review which cables, remotes, covers or other accessories are supplied.",
  icon: Layers
}];
export default function PackageContents() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="packagecontents" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 border-b border-border">
      {/* Blueprint Grid Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
      backgroundImage: "linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} />

      <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 border border-border bg-muted/60 px-3 py-1 text-xs font-mono tracking-wider uppercase text-muted-foreground mb-4">
            <span className="h-1.5 w-1.5 bg-primary" />
            What's Included
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight text-foreground uppercase mb-6">
            What Comes With an Inflatable Mechanical Bull Package?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Package contents vary, which is why two similar-looking systems can have very different prices. Before comparing one inflatable mechanical bull for sale with another, confirm exactly what the advertised price includes.
          </p>
        </div>

        {/* 5 Blueprint Cards Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }}>
          {packageItems.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} variants={itemVariants} className="group relative bg-card text-card-foreground border border-border p-6 md:p-7 shadow-xs hover:border-primary transition-colors flex flex-col justify-between">
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2.5 border border-border bg-muted text-foreground shrink-0 group-hover:border-primary group-hover:text-primary transition-colors">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-mono text-base md:text-lg font-bold tracking-tight uppercase text-foreground leading-snug pt-1">
                      {item.title}
                    </h3>
                  </div>

                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed pt-1">
                    {item.desc}
                  </p>
                </div>
              </motion.div>;
        })}

          {/* Complementary CTA Card filling the 6th grid slot on lg screens */}
          <motion.div data-index={5} variants={itemVariants} className="bg-secondary text-secondary-foreground border border-secondary p-6 md:p-7 flex flex-col justify-between">
            <div>
              <h3 className="font-mono text-lg font-bold uppercase tracking-tight text-secondary-foreground mb-3">
                Need Help Reviewing a Package?
              </h3>
              <p className="text-sm text-secondary-foreground/80 leading-relaxed mb-6">
                Send us the listing or product you are reviewing and we can review the available package information with you, including the controller, inflatable, blower and other included equipment.
              </p>
            </div>
            <div className="pt-4">
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-11 font-mono text-xs tracking-wider uppercase font-semibold justify-between px-4">
                <Link to="/RequestQuotePage">
                  <span>Request Package Information</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>

        {/* Highlighted Warning / Callout Banner */}
        <div className="border border-primary bg-primary/5 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-5 shadow-xs">
          <div className="p-2.5 bg-primary text-primary-foreground shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold uppercase tracking-wider text-primary">
                BUYER NOTICE
              </span>
            </div>
            <p className="text-sm md:text-base font-medium text-foreground">
              If an item is not shown as included, ask before assuming it comes with
              the machine.
            </p>
          </div>
        </div>
      </div>
    </section>;
}